// ===========================================
// Calendar Page with Drag-and-Drop
// ===========================================
// Displays all lessons with hybrid placeholders using react-big-calendar
// Supports drag-and-drop rescheduling with conflict detection

import { useState, useMemo, useCallback, useEffect, useRef, TouchEvent as ReactTouchEvent } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Calendar, View, Views } from 'react-big-calendar';
import withDragAndDrop, {
  EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';
import { addDays, format } from 'date-fns';
import {
  Box,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Menu,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useSnackbar } from 'notistack';
import PageHeader from '../../components/common/PageHeader';
import { useCalendarEvents } from '../../hooks/useHybridBooking';
import { termWeekCount, lessonWeekWindow } from '../../utils/dateUtils';
import {
  useLocations,
  useRooms,
  useLessonTypes,
  useSchoolSettings,
  useUpdateSchoolSettings,
} from '../../hooks/useAdmin';
import { useViewTerms } from '../../hooks/useViewTerms';
import { useViewInstruments, useViewProgramLevels } from '../../hooks/useViewOptions';
import { useTeachers } from '../../hooks/useUsers';
import { useAuth } from '../../contexts/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { isTeacherViewPath } from '../../components/layout/effectiveView';
import {
  useCheckRescheduleConflicts,
  useRescheduleLesson,
  useRelatedLessons,
  useLesson,
  useCheckSingleOccurrenceConflicts,
  useRescheduleSingleOccurrence,
  useCancelSingleOccurrence,
} from '../../hooks/useLessons';
import { CalendarEvent, CalendarEventsFilters } from '../../api/hybridBooking.api';
import SchoolEventDialog from '../../components/events/SchoolEventDialog';
import CalendarEventsStrip from '../../components/admin/calendar/CalendarEventsStrip';
import CalendarDisplaySettingsModal, {
  CalendarDisplaySettings,
  DEFAULT_CALENDAR_DISPLAY_SETTINGS,
  formatTeacherName,
} from '../../components/admin/CalendarDisplaySettingsModal';
import {
  CalendarToolbar,
  CalendarFilters,
  CalendarEventDialog,
  CalendarRescheduleDialog,
  CalendarCancelDialog,
  CalendarResourceHeader,
  CalendarEventContextMenu,
  localizer,
  CALENDAR_MIN_TIME,
  CALENDAR_MAX_TIME,
  calendarPaperStyles,
  DRAGGABLE_TYPES,
  formatTimeForApi,
  originalDateKeyForEvent,
  buildOccurrencePayload,
  roomResourceTitle,
  applyEventLabel,
  getEventStyle,
  isEventDraggable,
  ResourceViewMode,
  CalendarResource,
  RescheduleDialogState,
  NewBookingType,
  SlotPrefill,
} from '../../components/admin/calendar';
import BookExtraLessonDialog from '../../components/admin/BookExtraLessonDialog';
import HybridPreBookDialog from '../../components/admin/HybridPreBookDialog';
import CreateMeetAndGreetDialog from '../../components/admin/CreateMeetAndGreetDialog';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

// Create drag-and-drop wrapped calendar with CalendarEvent type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DragAndDropCalendar = withDragAndDrop<CalendarEvent>(Calendar as any);

// Module-level refs for event handlers. Updated each render by CalendarPage.
// EventWrapper and CalendarEventComponent are defined outside CalendarPage to avoid
// unmount/remount when handlers change.
const eventContextMenuRef: { current: ((event: CalendarEvent, e: React.MouseEvent) => void) | null } = { current: null };
const eventSelectRef: { current: ((event: CalendarEvent) => void) | null } = { current: null };
const LONG_PRESS_MS = 500;

function EventWrapper({ event, children }: { event: CalendarEvent; children: React.ReactNode }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchPosRef = useRef<{ x: number; y: number } | null>(null);

  // Clear timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleTouchStart = useCallback((e: ReactTouchEvent) => {
    const touch = e.touches[0];
    touchPosRef.current = { x: touch.clientX, y: touch.clientY };
    timerRef.current = setTimeout(() => {
      if (!touchPosRef.current) return;
      // Synthesize a mouse event for the context menu handler
      const syntheticEvent = {
        preventDefault: () => {},
        clientX: touchPosRef.current.x,
        clientY: touchPosRef.current.y,
      } as React.MouseEvent;
      eventContextMenuRef.current?.(event, syntheticEvent);
      touchPosRef.current = null;
    }, LONG_PRESS_MS);
  }, [event]);

  const handleTouchEnd = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    touchPosRef.current = null;
  }, []);

  const handleTouchMove = useCallback((e: ReactTouchEvent) => {
    // Cancel long-press if finger moves more than 10px
    if (touchPosRef.current && timerRef.current) {
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - touchPosRef.current.x);
      const dy = Math.abs(touch.clientY - touchPosRef.current.y);
      if (dx > 10 || dy > 10) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        touchPosRef.current = null;
      }
    }
  }, []);

  return (
    <div
      onContextMenu={(e) => eventContextMenuRef.current?.(event, e)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onTouchMove={handleTouchMove}
    >
      {children}
    </div>
  );
}

// Inner event component — the title is a clickable link; the coloured background drags
function CalendarEventComponent({ event, title }: { event: CalendarEvent; title: string }) {
  const teacherColor = event.resource?.teacherColor;
  const isCancelled = event.resource?.isCancelled === true;
  const isAbsenteeReleased = event.resource?.isAbsenteeReleased === true;

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%' }}>
      {teacherColor && (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: teacherColor,
            border: '1px solid rgba(0,0,0,0.15)',
            flexShrink: 0,
            display: 'inline-block',
            opacity: isCancelled || isAbsenteeReleased ? 0.5 : 1,
          }}
        />
      )}
      <span
        className={isCancelled ? 'cancelled-event-text' : ''}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textDecoration: isCancelled ? 'line-through' : 'none',
          fontStyle: isAbsenteeReleased ? 'italic' : undefined,
          cursor: 'pointer',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          eventSelectRef.current?.(event);
        }}
      >
        {isAbsenteeReleased ? `${title} (Absent)` : title}
      </span>
    </span>
  );
}

// ===========================================
// COMPONENT
// ===========================================

export default function CalendarPage() {
  // State
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const location = useLocation();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();
  const { user } = useAuth();

  // Determine base path for navigation (admin vs teacher)
  const isTeacherView = isTeacherViewPath(location.pathname);
  const { can } = usePermissions();
  // Roles 1b §5.15: drag, resize, slot selection and "+ New booking" follow the server's gate
  // (`scheduling.write`) and never appear in the teacher view.
  const mayEditSchedule = can('scheduling', 'write') && !isTeacherView;
  const basePath = schoolSlug
    ? `/${schoolSlug}/${isTeacherView ? 'teacher' : 'admin'}`
    : isTeacherView ? '/teacher' : '/admin';

  // Teacher view: toggle between own lessons ('my') and full school schedule ('all')
  const [teacherScopeMode, setTeacherScopeMode] = useState<'my' | 'all'>(() => {
    try {
      const saved = localStorage.getItem('teacherCalendarScope');
      if (saved === 'all' || saved === 'my') return saved;
    } catch { /* ignore */ }
    return 'my';
  });

  const [view, setView] = useState<View>(() => {
    try {
      const saved = localStorage.getItem('calendarViewPreference');
      if (saved && ([Views.MONTH, Views.WEEK, Views.DAY] as string[]).includes(saved)) {
        return saved as View;
      }
    } catch {
      // Ignore localStorage errors
    }
    return Views.DAY;
  });
  const [date, setDate] = useState(new Date());
  const [filters, setFilters] = useState<CalendarEventsFilters>({});
  const [hideHybridGroup, setHideHybridGroup] = useState<boolean>(() => {
    try {
      return localStorage.getItem('calendarHideHybridGroup') === 'true';
    } catch {
      return false;
    }
  });
  const [resourceViewMode, setResourceViewMode] = useState<ResourceViewMode>('teacher');
  const [rescheduleDialog, setRescheduleDialog] = useState<RescheduleDialogState>({
    open: false,
    event: null,
    newStart: null,
    newEnd: null,
    originalDateKey: null,
    conflicts: null,
    loading: false,
    error: null,
  });
  const [notifyParents, setNotifyParents] = useState(true);
  // Whether the admin has explicitly touched the notify switch for THIS reschedule.
  // Until they do, a drag of a PAST occurrence sends no flag at all so the server's
  // past-aware default applies (resolveNotifyParents → silent for a class that
  // already happened). Without this the calendar would always send an explicit
  // `true` and email families about a weeks-old lesson — the drag path was the
  // original way past occurrences were reachable, so it must not bypass the rule.
  const [notifyTouched, setNotifyTouched] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [applyToAllTerms, setApplyToAllTerms] = useState(false);
  const [overrideConflicts, setOverrideConflicts] = useState(false);
  const [rescheduleMode, setRescheduleMode] = useState<'single' | 'all'>('single');
  const [fromWeekNumber, setFromWeekNumber] = useState<number | undefined>();

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    position: { top: number; left: number } | null;
    event: CalendarEvent | null;
  }>({ position: null, event: null });

  // Cancel occurrence dialog state
  const [cancelDialog, setCancelDialog] = useState<{
    open: boolean;
    event: CalendarEvent | null;
    reason: string;
    notifyParents: boolean;
  }>({ open: false, event: null, reason: '', notifyParents: true });

  // Calendar display settings
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [displaySettings, setDisplaySettings] = useState<CalendarDisplaySettings>(() => {
    const saved = localStorage.getItem('calendarDisplaySettings');
    if (saved) {
      try {
        return { ...DEFAULT_CALENDAR_DISPLAY_SETTINGS, ...JSON.parse(saved) };
      } catch {
        return DEFAULT_CALENDAR_DISPLAY_SETTINGS;
      }
    }
    return DEFAULT_CALENDAR_DISPLAY_SETTINGS;
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsModalTab, setSettingsModalTab] = useState(0);

  // "+ New booking" menu state
  const [newBookingDialog, setNewBookingDialog] = useState<NewBookingType | null>(null);
  const [pendingPrefill, setPendingPrefill] = useState<SlotPrefill | null>(null);
  const [newBookingAnchor, setNewBookingAnchor] = useState<null | HTMLElement>(null);

  const initialSyncDone = useRef(false);

  // Snackbar for toast notifications
  const { enqueueSnackbar } = useSnackbar();

  // Queries. Each admin list read runs only for a caller its route admits (#272): a plain teacher
  // used to get six 403s on every visit. The school's display defaults stay office-only (plan D3);
  // instruments and program levels fall back to the calendar picker lists teachers may read (D4).
  const canReadSchoolSetup = can('school_setup', 'read');
  const canReadClassConfig = can('class_config', 'read');
  const { data: schoolSettings } = useSchoolSettings({ enabled: canReadSchoolSetup });
  const updateSchoolSettings = useUpdateSchoolSettings();
  const { data: teachersData } = useTeachers();

  // Sync display settings from server on initial load only
  useEffect(() => {
    if (initialSyncDone.current) return;
    if (schoolSettings?.settings?.calendarDisplay) {
      const serverSettings = schoolSettings.settings.calendarDisplay as CalendarDisplaySettings;
      if (
        serverSettings.labelSource &&
        serverSettings.backgroundColorSource &&
        serverSettings.borderColorSource
      ) {
        const merged = { ...DEFAULT_CALENDAR_DISPLAY_SETTINGS, ...serverSettings };
        setDisplaySettings(merged);
        localStorage.setItem('calendarDisplaySettings', JSON.stringify(merged));
      }
      initialSyncDone.current = true;
    }
  }, [schoolSettings]);

  const { data: locationsData } = useLocations({ enabled: canReadSchoolSetup });
  const { data: roomsData } = useRooms(filters.locationId, { enabled: canReadSchoolSetup });
  const { data: allRoomsData } = useRooms(undefined, { enabled: canReadSchoolSetup });
  const { data: instrumentsData } = useViewInstruments();
  const { data: lessonTypesData } = useLessonTypes({ enabled: canReadClassConfig });
  const { data: programLevelsData } = useViewProgramLevels();
  const { data: termsData } = useViewTerms(); // the teacher view's week labels need terms too (#272)

  const teachers = teachersData ?? [];
  const locations = locationsData ?? [];
  const rooms = roomsData ?? [];
  const allRooms = allRoomsData ?? [];
  const instruments = instrumentsData ?? [];
  const lessonTypes = lessonTypesData ?? [];
  const programLevels = programLevelsData ?? [];
  // Week labels only: a deactivated term (soft delete) never numbers a week (#272).
  const terms = useMemo(() => (termsData ?? []).filter((t) => t.isActive), [termsData]);

  // Find current teacher's record if logged in as a teacher.
  // Prefer the teacherId surfaced by the auth user payload: it needs no list lookup, and the
  // "My Lessons" filter must work before the teachers list (`GET /teachers`, which plain
  // teachers may read via teacherOrPermission) has loaded.
  const currentTeacherId = user?.teacherId ?? null;
  const currentTeacher = useMemo(() => {
    if (!currentTeacherId || teachers.length === 0) return null;
    return teachers.find((t) => t.id === currentTeacherId) ?? null;
  }, [currentTeacherId, teachers]);

  // Instruments the current teacher teaches (used to narrow the instrument filter
  // when viewing their own lessons)
  const currentTeacherInstrumentIds = useMemo(() => {
    if (!currentTeacher) return null;
    return new Set(currentTeacher.TeacherInstrument.map((ti) => ti.Instrument.id));
  }, [currentTeacher]);

  // Instruments to show in the filter — narrowed to the teacher's own instruments
  // when in "My Lessons" mode, otherwise all instruments.
  const filterInstruments = useMemo(() => {
    if (isTeacherView && teacherScopeMode === 'my' && currentTeacherInstrumentIds) {
      return instruments.filter((i) => currentTeacherInstrumentIds.has(i.id));
    }
    return instruments;
  }, [instruments, isTeacherView, teacherScopeMode, currentTeacherInstrumentIds]);

  // Effective filters - include teacher filter for teacher view when scoped to "my"
  const effectiveFilters = useMemo((): CalendarEventsFilters => {
    if (isTeacherView && currentTeacherId && teacherScopeMode === 'my') {
      return { ...filters, teacherId: currentTeacherId };
    }
    return filters;
  }, [filters, isTeacherView, currentTeacherId, teacherScopeMode]);

  const handleTeacherScopeChange = useCallback((_e: React.SyntheticEvent, value: 'my' | 'all') => {
    setTeacherScopeMode(value);
    try { localStorage.setItem('teacherCalendarScope', value); } catch { /* ignore */ }
    // When switching to "My Lessons", drop the (now-hidden) teacher filter and any
    // instrument filter that isn't in the teacher's narrowed instrument list.
    if (value === 'my') {
      setFilters((prev) => {
        const next = { ...prev, teacherId: undefined };
        if (next.instrumentId && currentTeacherInstrumentIds && !currentTeacherInstrumentIds.has(next.instrumentId)) {
          next.instrumentId = undefined;
        }
        return next;
      });
    }
  }, [currentTeacherInstrumentIds]);

  // Mutations
  const checkConflicts = useCheckRescheduleConflicts();
  const rescheduleLesson = useRescheduleLesson();
  const checkSingleConflicts = useCheckSingleOccurrenceConflicts();
  const rescheduleSingleOccurrence = useRescheduleSingleOccurrence();
  const cancelSingleOccurrence = useCancelSingleOccurrence();

  // Get related lessons for the currently selected event
  const { data: relatedLessons } = useRelatedLessons(
    rescheduleDialog.event?.resource?.lessonId || null
  );

  // The dragged class's own row, fetched only while the "All classes" dialog is open: the
  // calendar event carries the term dates but not the class window (startWeek/endWeek), and
  // "Apply from week" must not offer a week the class does not run.
  const draggedLessonId =
    rescheduleDialog.open && rescheduleMode === 'all'
      ? rescheduleDialog.event?.resource?.lessonId || ''
      : '';
  const {
    data: draggedLesson,
    isLoading: draggedLessonLoading,
    isError: draggedLessonError,
  } = useLesson(draggedLessonId);
  const rescheduleWeekWindow =
    draggedLesson && rescheduleDialog.totalWeeks
      ? lessonWeekWindow(draggedLesson, rescheduleDialog.totalWeeks)
      : undefined;
  // Settled? Until it is, the dialog keeps "Apply from week" disabled (else every term week
  // is offered, and a pick could vanish when the window arrives).
  const rescheduleWeekWindowStatus: 'loading' | 'ready' | 'error' = draggedLessonError
    ? 'error'
    : draggedLessonLoading || (!!draggedLessonId && !draggedLesson)
      ? 'loading'
      : 'ready';

  // Calculate date range for events query
  const dateRange = useMemo(() => {
    const start = addDays(date, -30);
    const end = addDays(date, 60);
    return {
      // Date KEYS, not local-time instants: an instant from a UTC+ browser lands on the
      // previous UTC day server-side (the shape TeacherDashboardPage already sends).
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
    };
  }, [date]);

  const {
    data: eventsData,
    isLoading: eventsLoading,
    error,
  } = useCalendarEvents({
    ...effectiveFilters,
    ...dateRange,
  });

  const events = eventsData ?? [];
  const teacherNameFormat = displaySettings.teacherNameFormat;

  // From the filter itself, never from a teacher-list lookup or a list length (#272, #527):
  // My Lessons must narrow before the teachers list has loaded (#669).
  const narrowedToOneTeacher = Boolean(effectiveFilters.teacherId);

  // Build resources for day view columns. Empty means one plain column: the calendar below then
  // omits the prop, because `resources={[]}` would draw no columns at all.
  const resources: CalendarResource[] = useMemo(() => {
    if (view !== Views.DAY || narrowedToOneTeacher) return [];

    if (resourceViewMode === 'teacher') {
      // Fallback palette for teachers without an assigned color
      const fallbackColors = [
        '#4580E4', '#96DAC9', '#FFAE9E', '#FFCE00',
        '#9C6ADE', '#47C1BF', '#F49342', '#5C6AC4',
      ];
      const fmt = teacherNameFormat;
      return teachers
        .filter((t) => t.isActive)
        .map((teacher, idx) => ({
          id: teacher.id,
          title: formatTeacherName(`${teacher.User.firstName} ${teacher.User.lastName}`, fmt),
          color: teacher.color || fallbackColors[idx % fallbackColors.length],
        }));
    }

    if (resourceViewMode === 'room') {
      return allRooms
        .filter((r) => r.isActive)
        .map((room) => ({
          id: room.id,
          title: roomResourceTitle(room),
          color: null,
        }));
    }

    return [];
  }, [view, narrowedToOneTeacher, resourceViewMode, teachers, allRooms, teacherNameFormat]);

  const handleHideHybridGroupChange = useCallback((value: boolean) => {
    setHideHybridGroup(value);
    try { localStorage.setItem('calendarHideHybridGroup', String(value)); } catch { /* ignore */ }
  }, []);

  // Client-side filter: optionally hide hybrid individual-week placeholders and absent/released slots
  const visibleEvents = useMemo(() => {
    if (!hideHybridGroup) return events;
    return events.filter(
      (e) => e.resource?.type !== 'HYBRID_PLACEHOLDER' && !e.resource?.isAbsenteeReleased
    );
  }, [events, hideHybridGroup]);

  // Map events to resources and apply display settings
  const eventsWithResources = useMemo(() => {
    // While the reschedule dialog is open, hide the dragged event from the
    // events array entirely — the DnD library's ghost preview will also have
    // cleared, so the calendar shows a clean slate until the user confirms.
    const pendingEventId = rescheduleDialog.open ? rescheduleDialog.event?.id : null;
    const filtered = pendingEventId
      ? visibleEvents.filter((e) => e.id !== pendingEventId)
      : visibleEvents;

    if (view !== Views.DAY || resources.length === 0) {
      return filtered.map((e) => applyEventLabel(e, displaySettings.labelSource, teacherNameFormat));
    }

    return filtered.map((event) => {
      let resourceId: string | undefined;

      if (resourceViewMode === 'teacher' && event.resource?.teacherId) {
        resourceId = event.resource.teacherId;
      } else if (resourceViewMode === 'room' && event.resource?.roomId) {
        resourceId = event.resource.roomId;
      }

      return {
        ...applyEventLabel(event, displaySettings.labelSource, teacherNameFormat),
        resourceId,
      };
    });
  }, [visibleEvents, view, resourceViewMode, resources, displaySettings.labelSource, teacherNameFormat, rescheduleDialog.open, rescheduleDialog.event]);

  // Handle resource view mode change
  const handleResourceViewModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newMode: ResourceViewMode | null) => {
      if (newMode !== null) {
        setResourceViewMode(newMode);
      }
    },
    []
  );

  // Handle settings button click
  const handleSettingsClick = useCallback(() => {
    setSettingsModalTab(0);
    setSettingsModalOpen(true);
  }, []);

  // Handle legend button click
  const handleLegendClick = useCallback(() => {
    setSettingsModalTab(1);
    setSettingsModalOpen(true);
  }, []);

  // Month view date header — shows "Wk N" on the left of Monday cells
  const MonthDateHeader = useMemo(() => {
    return function MonthDateHeader({ date, label, drilldownView, onDrillDown }: {
      date: Date; label: string; drilldownView?: string; onDrillDown?: () => void;
    }) {
      const isMonday = date.getDay() === 1;
      let weekLabel: string | null = null;
      if (isMonday) {
        // Use local date parts to avoid timezone shift when comparing calendar cell dates
        const toLocalDay = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
        const ms = toLocalDay(date);
        for (const term of terms) {
          // term.startDate is a date string (e.g. "2026-04-20") — parse as local midnight
          const [sy, sm, sd] = term.startDate.split('T')[0].split('-').map(Number);
          const [ey, em, ed] = term.endDate.split('T')[0].split('-').map(Number);
          const start = Date.UTC(sy, sm - 1, sd);
          const end = Date.UTC(ey, em - 1, ed);
          if (ms >= start && ms <= end) {
            // Monday-anchored weeks (termWeekAnchor): number from the Monday
            // of the term-start week so labels match the canonical numbering.
            const anchor = start - ((new Date(start).getUTCDay() + 6) % 7) * 86400000;
            weekLabel = `Wk ${Math.floor((ms - anchor) / (7 * 24 * 60 * 60 * 1000)) + 1}`;
            break;
          }
        }
      }
      const inner = drilldownView
        ? <button type="button" className="rbc-button-link" onClick={onDrillDown}>{label}</button>
        : <span>{label}</span>;
      if (!weekLabel) return inner;
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span style={{ fontSize: '0.7em', color: '#9DA5AF', fontWeight: 500, lineHeight: 1 }}>{weekLabel}</span>
          {inner}
        </div>
      );
    };
  }, [terms]);

  // Event style getter
  const eventStyleGetter = useCallback(
    (event: CalendarEvent) => getEventStyle(event, displaySettings),
    [displaySettings]
  );

  // Handle event click
  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    setSelectedEvent(event);
  }, []);

  // Handle right-click on calendar events
  const handleEventContextMenu = useCallback((event: CalendarEvent, e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({
      position: { top: e.clientY, left: e.clientX },
      event,
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({ position: null, event: null });
  }, []);

  // Update module-level refs so the stable EventWrapper always uses latest handlers
  eventContextMenuRef.current = handleEventContextMenu;
  eventSelectRef.current = handleSelectEvent;

  // Handle view change
  const handleViewChange = useCallback((newView: View) => {
    setView(newView);
    try {
      localStorage.setItem('calendarViewPreference', newView);
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Handle navigation
  const handleNavigate = useCallback((newDate: Date) => {
    setDate(newDate);
  }, []);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (field: keyof CalendarEventsFilters) => (event: SelectChangeEvent<string>) => {
      const value = event.target.value;
      setFilters((prev) => ({
        ...prev,
        [field]: value || undefined,
      }));
    },
    []
  );

  // Handle location change (resets room)
  const handleLocationChange = useCallback((e: SelectChangeEvent<string>) => {
    setFilters((prev) => ({
      ...prev,
      locationId: e.target.value || undefined,
      roomId: undefined,
    }));
  }, []);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Handle event drag
  const handleEventDrop = useCallback(
    async ({ event, start, end }: EventInteractionArgs<CalendarEvent>) => {
      const eventType = event.resource?.type;
      const lessonId = event.resource?.lessonId;

      if (!eventType || !DRAGGABLE_TYPES.includes(eventType) || !lessonId) {
        return;
      }

      const newStart = new Date(start);
      const newEnd = new Date(end);
      // The occurrence's IDENTITY KEY. Always a backend-supplied calendar-day key —
      // `resource.originalDate` for an already-rescheduled event, else the event's own
      // `dateKey`. Never re-derive it from `event.start`: that is a LOCAL render instant,
      // and serialising it shifts the key to the previous day for morning classes east of
      // UTC, orphaning the real occurrence behind a phantom exception row.
      const originalDateKey = originalDateKeyForEvent(event);

      setRescheduleMode('single');
      setRescheduleDialog({
        open: true,
        event,
        newStart,
        newEnd,
        originalDateKey,
        conflicts: null,
        loading: true,
        error: null,
      });

      try {
        // Default to single-occurrence conflict check
        const conflicts = await checkSingleConflicts.mutateAsync({
          lessonId,
          input: {
            ...buildOccurrencePayload(event, newStart, newEnd),
          },
        });

        setRescheduleDialog((prev) => ({
          ...prev,
          conflicts,
          loading: false,
        }));
      } catch {
        setRescheduleDialog((prev) => ({
          ...prev,
          loading: false,
          error: 'Failed to check for conflicts. Please close and try again.',
        }));
      }
    },
    [checkSingleConflicts]
  );

  // Handle resize - only changes duration, always single-occurrence
  const handleEventResize = useCallback(
    async ({ event, start, end }: EventInteractionArgs<CalendarEvent>) => {
      const eventType = event.resource?.type;
      const lessonId = event.resource?.lessonId;

      if (!eventType || !DRAGGABLE_TYPES.includes(eventType) || !lessonId) {
        return;
      }

      const newStart = new Date(start);
      const newEnd = new Date(end);
      const originalDateKey = originalDateKeyForEvent(event);

      // Resize always uses single-occurrence mode (only changes duration)
      setRescheduleMode('single');
      setRescheduleDialog({
        open: true,
        event,
        newStart,
        newEnd,
        originalDateKey,
        conflicts: null,
        loading: true,
        error: null,
      });

      try {
        const conflicts = await checkSingleConflicts.mutateAsync({
          lessonId,
          input: {
            ...buildOccurrencePayload(event, newStart, newEnd),
          },
        });

        setRescheduleDialog((prev) => ({
          ...prev,
          conflicts,
          loading: false,
        }));
      } catch {
        setRescheduleDialog((prev) => ({
          ...prev,
          loading: false,
          error: 'Failed to check for conflicts. Please close and try again.',
        }));
      }
    },
    [checkSingleConflicts]
  );

  // Handle mode change (single vs all occurrences)
  const handleRescheduleModeChange = useCallback(
    async (mode: 'single' | 'all') => {
      setRescheduleMode(mode);
      const { event, newStart, newEnd, originalDateKey } = rescheduleDialog;
      if (!event || !newStart || !newEnd || !event.resource?.lessonId) return;

      setRescheduleDialog((prev) => ({ ...prev, loading: true, conflicts: null, error: null }));

      try {
        if (mode === 'single' && originalDateKey) {
          const conflicts = await checkSingleConflicts.mutateAsync({
            lessonId: event.resource.lessonId,
            input: {
              ...buildOccurrencePayload(event, newStart, newEnd),
            },
          });
          setRescheduleDialog((prev) => ({ ...prev, conflicts, loading: false }));
        } else {
          const conflicts = await checkConflicts.mutateAsync({
            lessonId: event.resource.lessonId,
            input: {
              newDayOfWeek: newStart.getDay(),
              newStartTime: formatTimeForApi(newStart),
              newEndTime: formatTimeForApi(newEnd),
            },
          });

          // Calculate term info for week selection (only in 'all' mode)
          if (mode === 'all' && event.resource?.termStartDate && event.resource?.termEndDate) {
            const termStartRaw = event.resource.termStartDate;
            const termEndRaw = event.resource.termEndDate;
            const termStart = new Date(termStartRaw);
            // Monday-anchored week count (#169) — was a third inline formula
            // (Math.ceil of the exclusive day span) that disagreed with the
            // pickers on a Monday-ending term.
            const totalWeeks = termWeekCount(termStartRaw, termEndRaw);
            setRescheduleDialog((prev) => ({
              ...prev,
              conflicts,
              loading: false,
              termStartDate: termStart,
              totalWeeks,
            }));
          } else {
            setRescheduleDialog((prev) => ({ ...prev, conflicts, loading: false }));
          }
        }
      } catch {
        setRescheduleDialog((prev) => ({
          ...prev,
          loading: false,
          error: 'Failed to check for conflicts. Please close and try again.',
        }));
      }
    },
    [rescheduleDialog, checkSingleConflicts, checkConflicts]
  );

  // Reset all reschedule-related state
  const resetRescheduleState = useCallback(() => {
    setRescheduleDialog({
      open: false,
      event: null,
      newStart: null,
      newEnd: null,
      originalDateKey: null,
      conflicts: null,
      loading: false,
      error: null,
      termStartDate: null,
      totalWeeks: undefined,
    });
    setRescheduleReason('');
    setNotifyParents(true);
    // Clear the manual-override flag too, or the past-aware default would never
    // apply again for the rest of this page's life.
    setNotifyTouched(false);
    setApplyToAllTerms(false);
    setOverrideConflicts(false);
    setRescheduleMode('single');
    setFromWeekNumber(undefined);
  }, []);

  // Confirm reschedule
  const handleConfirmReschedule = useCallback(async () => {
    const { event, newStart, newEnd, originalDateKey } = rescheduleDialog;
    if (!event || !newStart || !newEnd || !event.resource?.lessonId) return;

    try {
      if (rescheduleMode === 'single' && originalDateKey) {
        await rescheduleSingleOccurrence.mutateAsync({
          lessonId: event.resource.lessonId,
          input: {
            ...buildOccurrencePayload(event, newStart, newEnd),
            // Omit when untouched so the server's past-aware default decides
            // (silent for an occurrence that already happened, notify otherwise).
            // Sending an explicit value here would bypass that rule entirely.
            notifyParents: notifyTouched ? notifyParents : undefined,
            reason: rescheduleReason || undefined,
            overrideConflicts: overrideConflicts || undefined,
          },
        });
      } else {
        await rescheduleLesson.mutateAsync({
          lessonId: event.resource.lessonId,
          input: {
            newDayOfWeek: newStart.getDay(),
            newStartTime: formatTimeForApi(newStart),
            newEndTime: formatTimeForApi(newEnd),
            notifyParents,
            reason: rescheduleReason || undefined,
            applyToAllTerms,
            fromWeekNumber,
            overrideConflicts: overrideConflicts || undefined,
          },
        });
      }

      resetRescheduleState();
    } catch {
      // Error handled by mutation hook
    }
  }, [rescheduleDialog, rescheduleMode, notifyParents, rescheduleReason, applyToAllTerms, fromWeekNumber, overrideConflicts, rescheduleLesson, rescheduleSingleOccurrence, resetRescheduleState]);

  // Cancel reschedule
  const handleCancelReschedule = resetRescheduleState;

  // Handle cancel occurrence (right-click context menu)
  const handleCancelOccurrence = useCallback((event: CalendarEvent) => {
    setCancelDialog({
      open: true,
      event,
      reason: '',
      notifyParents: true,
    });
  }, []);

  const handleConfirmCancelOccurrence = useCallback(async () => {
    const { event, reason, notifyParents: notify } = cancelDialog;
    if (!event || !event.resource?.lessonId) return;

    try {
      // Same identity key as the reschedule path — a cancel targets the stored
      // `originalDate`, never the rendered date.
      const cancelOriginalDateKey = originalDateKeyForEvent(event);
      await cancelSingleOccurrence.mutateAsync({
        lessonId: event.resource.lessonId,
        input: {
          originalDate: cancelOriginalDateKey,
          reason: reason || undefined,
          notifyParents: notify,
        },
      });
      setCancelDialog({ open: false, event: null, reason: '', notifyParents: true });
    } catch {
      // Error handled by mutation hook
    }
  }, [cancelDialog, cancelSingleOccurrence]);

  const handleCloseCancelDialog = useCallback(() => {
    setCancelDialog({ open: false, event: null, reason: '', notifyParents: true });
  }, []);

  // Save display settings — teachers can customise their own calendar view via
  // localStorage only (the school-wide settings PATCH is admin-gated).
  const handleSaveDisplaySettings = useCallback(
    async (newSettings: CalendarDisplaySettings) => {
      setSettingsSaving(true);
      try {
        setDisplaySettings(newSettings);
        localStorage.setItem('calendarDisplaySettings', JSON.stringify(newSettings));

        // The school-wide copy needs `school_setup.write` (the PATCH's gate); without it the
        // settings stay in this browser, and the message says so (/qa step 6 L2).
        const saveForSchool = !isTeacherView && can('school_setup', 'write');
        if (saveForSchool) {
          // Only `calendarDisplay` (roles plan R20): the server never reads another settings key.
          await updateSchoolSettings.mutateAsync({
            settings: { calendarDisplay: newSettings as unknown as Record<string, unknown> },
          });
        }

        setSettingsModalOpen(false);
        enqueueSnackbar(
          saveForSchool || isTeacherView
            ? 'Calendar display settings saved'
            : 'Calendar display settings saved for this browser only',
          { variant: 'success' }
        );
      } catch (error) {
        console.error('Failed to save calendar settings to server:', error);
        setSettingsModalOpen(false);
        enqueueSnackbar('Settings saved locally but failed to sync to server', {
          variant: 'warning',
        });
      } finally {
        setSettingsSaving(false);
      }
    },
    [updateSchoolSettings, enqueueSnackbar, isTeacherView, can]
  );

  const isLoading = eventsLoading;

  return (
    <Box>
      <PageHeader
        title="Calendar"
        subtitle="View and reschedule lessons with drag-and-drop"
        breadcrumbs={isTeacherView ? undefined : [{ label: 'Admin', path: basePath }, { label: 'Calendar' }]}
      />

      {/* Teacher scope tabs — switch between own lessons and full school schedule */}
      {isTeacherView && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={teacherScopeMode} onChange={handleTeacherScopeChange}>
            <Tab label="My Lessons" value="my" />
            <Tab label="Entire School" value="all" />
          </Tabs>
        </Box>
      )}

      {/* Drag hint + "+ New booking" action — admin only */}
      {mayEditSchedule && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 2,
            alignItems: 'stretch',
          }}
        >
          <Alert severity="info" icon={<DragIndicatorIcon />} sx={{ flex: { sm: 1 }, mb: 0 }}>
            Drag and drop lessons to reschedule them. Hybrid placeholders and Meet &amp; Greets
            cannot be moved.
          </Alert>
          {mayEditSchedule && (
            <>
              <Button
                variant="contained"
                size="medium"
                startIcon={<AddIcon />}
                endIcon={<ArrowDropDownIcon />}
                onClick={(e) => setNewBookingAnchor(e.currentTarget)}
                sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, whiteSpace: 'nowrap' }}
              >
                New booking
              </Button>
              <Menu
                anchorEl={newBookingAnchor}
                open={Boolean(newBookingAnchor)}
                onClose={() => setNewBookingAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setNewBookingAnchor(null);
                    setNewBookingDialog('EXTRA_LESSON');
                  }}
                >
                  Extra Lesson
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setNewBookingAnchor(null);
                    setNewBookingDialog('HYBRID_PRE_BOOK');
                  }}
                >
                  Hybrid Individual Pre-Book
                </MenuItem>
                {/* Its own gate: the create route needs `meet_and_greet.write` (/qa step 6 M1). */}
                {can('meet_and_greet', 'write') && (
                  <MenuItem
                    onClick={() => {
                      setNewBookingAnchor(null);
                      setNewBookingDialog('MEET_AND_GREET');
                    }}
                  >
                    Meet &amp; Greet
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load calendar events. Please try again.
        </Alert>
      )}

      {/* Filters - teachers in "My Lessons" mode see the same filters as admins,
          minus the Teacher dropdown and with instruments narrowed to what they teach. */}
      <CalendarFilters
        filters={filters}
        teachers={teachers}
        locations={locations}
        rooms={rooms}
        instruments={filterInstruments}
        onFilterChange={handleFilterChange}
        onLocationChange={handleLocationChange}
        onClearFilters={handleClearFilters}
        hideTeacherDropdown={isTeacherView && teacherScopeMode === 'my'}
      />

      {/* School events have no teacher/room column, so list them above the Day resource view */}
      {view === Views.DAY && resources.length > 0 && (
        <CalendarEventsStrip events={visibleEvents} date={date} resourceViewMode={resourceViewMode} onSelect={handleSelectEvent} />
      )}

      {/* Calendar */}
      <Paper sx={calendarPaperStyles} className={rescheduleDialog.open ? 'rbc-reschedule-pending' : undefined}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <DragAndDropCalendar
          localizer={localizer}
          events={eventsWithResources}
          startAccessor="start"
          endAccessor="end"
          formats={{
            dayHeaderFormat: (date: Date) =>
              date.toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short' }),
            dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
              `${start.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })} – ${end.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' })}`,
            monthHeaderFormat: (date: Date) =>
              date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
          }}
          view={view}
          onView={handleViewChange}
          date={date}
          onNavigate={handleNavigate}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={handleSelectEvent}
          onEventDrop={mayEditSchedule ? handleEventDrop : undefined}
          onEventResize={mayEditSchedule ? handleEventResize : undefined}
          draggableAccessor={mayEditSchedule ? isEventDraggable : () => false}
          resizable={mayEditSchedule}
          selectable={mayEditSchedule}
          onSelectSlot={(slotInfo) => {
            // Stash the slot for the next "+ New booking" menu click. Cleared on dialog close.
            setPendingPrefill({
              start: new Date(slotInfo.start),
              end: new Date(slotInfo.end),
              resourceId: slotInfo.resourceId
                ? String(slotInfo.resourceId)
                : undefined,
            });
          }}
          step={15}
          timeslots={4}
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          min={CALENDAR_MIN_TIME}
          max={CALENDAR_MAX_TIME}
          scrollToTime={CALENDAR_MIN_TIME}
          style={{ height: '100%', opacity: isLoading ? 0.5 : 1 }}
          popup
          showMultiDayTimes
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          components={{
            toolbar: (props: any) => (
              <CalendarToolbar
                {...props}
                resourceViewMode={resourceViewMode}
                onResourceViewModeChange={handleResourceViewModeChange}
                hideResourceToggle={narrowedToOneTeacher}
                onSettingsClick={handleSettingsClick}
                onLegendClick={handleLegendClick}
                terms={terms}
              />
            ),
            resourceHeader: CalendarResourceHeader as any,
            dateHeader: MonthDateHeader as any,
            event: CalendarEventComponent as any,
            eventWrapper: EventWrapper as any, // Stable component defined at module scope
          } as any}
          {...(view === Views.DAY && resources.length > 0
            ? {
                resources,
                resourceIdAccessor: (resource: object) => (resource as CalendarResource).id,
                resourceTitleAccessor: (resource: object) =>
                  (resource as CalendarResource).title,
              }
            : {})}
        />
      </Paper>

      {/* Event Detail Dialog */}
      <SchoolEventDialog
        event={selectedEvent?.resource?.type === 'EVENT' ? selectedEvent : null}
        onClose={() => setSelectedEvent(null)}
      />
      <CalendarEventDialog
        event={selectedEvent?.resource?.type === 'EVENT' ? null : selectedEvent}
        displaySettings={displaySettings}
        basePath={basePath}
        onClose={() => setSelectedEvent(null)}
      />

      {/* "+ New booking" toolbar dialogs. Mounted only when they can open: their only opener is
          the "+ New booking" menu (mayEditSchedule; the Meet & Greet item also needs
          meet_and_greet.write), and mounted closed they still ran their admin reads, which 403
          for a teacher (#272). */}
      {mayEditSchedule && (
        <>
          <BookExtraLessonDialog
            open={newBookingDialog === 'EXTRA_LESSON'}
            onClose={() => {
              setNewBookingDialog(null);
              setPendingPrefill(null);
            }}
            prefill={
              pendingPrefill
                ? { date: pendingPrefill.start }
                : undefined
            }
          />
          <HybridPreBookDialog
            open={newBookingDialog === 'HYBRID_PRE_BOOK'}
            onClose={() => {
              setNewBookingDialog(null);
              setPendingPrefill(null);
            }}
            prefill={
              pendingPrefill ? { date: pendingPrefill.start } : undefined
            }
          />
          {can('meet_and_greet', 'write') && (
            <CreateMeetAndGreetDialog
              open={newBookingDialog === 'MEET_AND_GREET'}
              onClose={() => {
                setNewBookingDialog(null);
                setPendingPrefill(null);
              }}
              prefill={
                pendingPrefill ? { date: pendingPrefill.start } : undefined
              }
            />
          )}
        </>
      )}

      {/* Reschedule Confirmation Dialog */}
      <CalendarRescheduleDialog
        dialog={rescheduleDialog}
        notifyParents={notifyParents}
        rescheduleReason={rescheduleReason}
        applyToAllTerms={applyToAllTerms}
        overrideConflicts={overrideConflicts}
        relatedLessons={relatedLessons}
        isPending={rescheduleLesson.isPending || rescheduleSingleOccurrence.isPending}
        rescheduleMode={rescheduleMode}
        fromWeekNumber={fromWeekNumber}
        weekWindow={rescheduleWeekWindow}
        weekWindowStatus={rescheduleWeekWindowStatus}
        onRescheduleModeChange={handleRescheduleModeChange}
        onNotifyParentsChange={(v: boolean) => {
          setNotifyParents(v);
          setNotifyTouched(true);
        }}
        onRescheduleReasonChange={setRescheduleReason}
        onApplyToAllTermsChange={setApplyToAllTerms}
        onOverrideConflictsChange={setOverrideConflicts}
        onFromWeekNumberChange={setFromWeekNumber}
        onConfirm={handleConfirmReschedule}
        onCancel={handleCancelReschedule}
      />

      {/* Cancel Occurrence Dialog */}
      <CalendarCancelDialog
        dialog={cancelDialog}
        isPending={cancelSingleOccurrence.isPending}
        onReasonChange={(reason) => setCancelDialog((prev) => ({ ...prev, reason }))}
        onNotifyParentsChange={(notifyParents) => setCancelDialog((prev) => ({ ...prev, notifyParents }))}
        onConfirm={handleConfirmCancelOccurrence}
        onCancel={handleCloseCancelDialog}
      />

      {/* Event Context Menu (right-click) */}
      <CalendarEventContextMenu
        anchorPosition={contextMenu.position}
        event={contextMenu.event}
        basePath={basePath}
        onClose={handleCloseContextMenu}
        onCancelOccurrence={handleCancelOccurrence}
        onViewDetails={handleSelectEvent}
      />

      {/* Calendar Display Settings Modal */}
      <CalendarDisplaySettingsModal
        open={settingsModalOpen}
        settings={displaySettings}
        loading={settingsSaving}
        onClose={() => setSettingsModalOpen(false)}
        onSave={handleSaveDisplaySettings}
        hideHybridGroup={hideHybridGroup}
        onHideHybridGroupChange={handleHideHybridGroupChange}
        lessonTypes={lessonTypes}
        teachers={teachers}
        instruments={instruments}
        programLevels={programLevels}
        initialTab={settingsModalTab}
      />
    </Box>
  );
}
